import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { ExclamationDiamondFill } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { OptionsType, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';

import { pathnames, requestCustomers } from '../../api';
import { getAxiosErrorResponse } from '../../api/utils';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import Customer, { CustomerPropType } from '../../models/data/Customer';

const FormatOptionLabel = ({ customerName, corporateCode }: Customer) => (
  <div className="d-flex align-items-baseline">
    <small className="text-secondary">
      {`${corporateCode.padStart(8, ' ')}`}
    </small>
    <span className="mx-4">{customerName}</span>
  </div>
);

export interface SelectCustomersProps {
  className?: string;
  isDisabled?: boolean;
  selectedCustomer?: Customer | null;
  onChange?: (customer: Customer | undefined | null) => void;
}

const SelectCustomers: React.FunctionComponent<SelectCustomersProps> = ({
  className,
  isDisabled,
  selectedCustomer,
  onChange,
}) => {
  const context = useContext(AuthenticatedContext);
  // ---------------------------------------------------------------------
  // Create WEB API URL.
  const history = useHistory();
  const url = history.createHref({ pathname: pathnames.customers });
  // ---------------------------------------------------------------------
  // Status
  const [cacheVersion, setCacheVersion] = useState(1);
  const [selectedOption, setSelectedOption] = useState<
    ValueType<Customer, false>
  >(selectedCustomer);
  // ---------------------------------------------------------------------
  // Callbacks & Functions
  const onChangeSelect = (option: ValueType<Customer, false>) => {
    setSelectedOption(option);
    if (onChange != null) {
      onChange(option);
    }
  };
  const toastError = (error: unknown) => {
    const res = getAxiosErrorResponse(error);
    toast.error(
      () => {
        return (
          <>
            <ExclamationDiamondFill />
            <span>{` 最終需要家 取得失敗`}</span>
            <small>{` (${res?.status ?? '応答なし'})`}</small>
          </>
        );
      },
      {
        onClose: () => {
          if (res != null && res.status >= 400 && res.status < 500) {
            // 認証エラーの可能性大、ログインチェック（期限切れの場合ログイン画面へ遷移する。はず）
            context.login().catch(() => {
              // do nothing.
            });
          }
        },
      },
    );
  };
  const loadOptions = (name: string): Promise<OptionsType<Customer>> => {
    if (isDisabled || name == null || name === '') {
      return Promise.resolve([]);
    }
    return requestCustomers(url, { name })
      .then(({ data: customers }) => {
        return customers;
      })
      .catch((error) => {
        toastError(error);
        throw error;
      });
  };
  // ---------------------------------------------------------------------
  // Effects
  useEffect(() => {
    setSelectedOption(selectedCustomer);
  }, [selectedCustomer]);
  useEffect(() => {
    setCacheVersion(cacheVersion + 1); // Purge cache request.
  }, [isDisabled]);
  // ---------------------------------------------------------------------
  // Components
  return (
    <AsyncSelect
      className={className}
      placeholder="最終需要家を入力・選択してください"
      isDisabled={isDisabled}
      defaultOptions={false}
      formatOptionLabel={FormatOptionLabel}
      cacheOptions={cacheVersion}
      loadOptions={loadOptions}
      value={selectedOption}
      onChange={onChangeSelect}
      getOptionLabel={(opt) => opt.customerName}
      getOptionValue={(opt) => String(opt.customerId)}
      isClearable
      styles={{
        // fix #1085
        menu: (zzz) => {
          return {
            ...zzz,
            zIndex: 9999,
          };
        },
      }}
    />
  );
};

SelectCustomers.propTypes = {
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  selectedCustomer: CustomerPropType,
  onChange: PropTypes.func,
};

SelectCustomers.defaultProps = {
  className: undefined,
  isDisabled: undefined,
  selectedCustomer: undefined,
  onChange: undefined,
};

export default SelectCustomers;
